import { useMemo, useState } from 'react';

import { CustomEventNames, EventTypes, logRBIEvent } from 'features/analytics';
import { updateAmplitudeUserAttributes } from 'features/profile/utils/analytics';

import ConfirmDialog from 'components/confirm-dialog';
import { useAuthContext } from 'state/auth';
import { t } from 'state/intl/translations';

import { ALLOW_REMOVE_LAST_FAVORITE_STORE } from './constants';

export function useFavoriteStores() {
  const { user } = useAuthContext();
  const existingFavoriteStores = useMemo(() => {
    const userFavoriteStores = user?.details?.favoriteStores || [];
    const trimmedFavoriteStores = userFavoriteStores.map(store => ({
      storeId: store.storeId,
      storeNumber: store.storeNumber || '',
    }));
    // We need to remove falsy ids that were previously saved from bad sanity data
    return trimmedFavoriteStores.filter(store => !!store.storeId);
  }, [user]);

  return {
    validFavStores: existingFavoriteStores,
  };
}
export const useFavoriteStore = (storeId: string, storeNumber: string) => {
  const { updateUserFavStores, isAuthenticated, openSignInModal } = useAuthContext();
  const { validFavStores } = useFavoriteStores();
  const [errorDialogActive, setErrorDialogActive] = useState(false);

  const [favStoreLoading, setFavStoreLoading] = useState(false);
  const [hasErrorRemovingLastFavStore, setHasErrorRemovingLastFavStore] = useState(false);

  const isFavorite = useMemo(() => !!validFavStores.find(store => store.storeId === storeId), [
    storeId,
    validFavStores,
  ]);

  const onDismissErrorRemovingLastFavStoreModal = () => {
    setHasErrorRemovingLastFavStore(false);
  };

  const toggleFavorite = async () => {
    if (!isAuthenticated) {
      setErrorDialogActive(true);
      return;
    }

    // Show error dialog when trying to remove the last favorite store if not allowed
    if (isFavorite && validFavStores.length === 1 && !ALLOW_REMOVE_LAST_FAVORITE_STORE) {
      setHasErrorRemovingLastFavStore(true);
      return;
    }

    const allFavoriteStores = isFavorite
      ? validFavStores.filter(store => store.storeId !== storeId)
      : [{ storeId, storeNumber }, ...validFavStores];
    // We need to remove falsy ids before we save to clean up bad sanity data
    const allValidFavoriteStores = allFavoriteStores.filter(store => !!store.storeId);
    setFavStoreLoading(true);
    await updateUserFavStores(allValidFavoriteStores);
    updateAmplitudeUserAttributes({
      favoriteStores: JSON.stringify(allValidFavoriteStores.map(store => store?.storeNumber)),
    });

    logRBIEvent(
      {
        name: CustomEventNames.STORE_FAVORITE_BUTTON_CLICKED,
        type: EventTypes.Other,
        attributes: {
          storeId,
          status: isFavorite ? 'unfavorited' : 'favorited',
        },
      },
      { skipLoggingToBraze: true }
    );

    setFavStoreLoading(false);
  };

  // TODO: Confirm with product if this is required as currently we're only displaying the
  // button to favorite a store if the user is authenticated
  const errorFavoriteStoreDialog = errorDialogActive ? (
    <ConfirmDialog
      showDialog
      heading={t('errorDialogFavoriteStoreTitle')}
      body={t('errorDialogFavoriteStoreMessage')}
      confirmButtonLabel={t('signUp')}
      onConfirm={() => {
        setErrorDialogActive(false);
        openSignInModal();
      }}
      onCancel={() => setErrorDialogActive(false)}
      modalAppearanceEventMessage="Error: Sign In to Set Favorite Stores"
      isErrorModal
    />
  ) : (
    <></>
  );

  return {
    toggleFavorite,
    isFavorite,
    errorFavoriteStoreDialog,
    favStoreLoading,
    hasErrorRemovingLastFavStore,
    onDismissErrorRemovingLastFavStoreModal,
  };
};
